import GLightbox from 'glightbox';
import Splide from '@splidejs/splide';

interface Lightbox {
  openAt: (index: number) => void;
}

interface LightboxElement {
  href: string,
  type: 'image'
}

function imgToLightboxElement(img: HTMLImageElement): LightboxElement {
  return {
    href: img.dataset.largeImage ?? img.src,
    type: 'image',
  };
}

export default class ProductGallery {
  static readonly SELECTOR = '.js-productGallery';

  private readonly primaryImageContainer: HTMLElement;
  private readonly primaryImage: HTMLImageElement;
  private readonly lightbox: Lightbox;

  private readonly thumbnailsContainer: HTMLElement | null;
  private readonly slider?: Splide;

  private readonly previousButton: HTMLButtonElement | null;
  private readonly nextButton: HTMLButtonElement | null;

  private readonly thumbnails: HTMLImageElement[];
  private index = 0;

  constructor(private container: HTMLElement) {
    this.primaryImageContainer = this.container.querySelector('.js-primaryImage') as HTMLElement;
    this.primaryImage = this.primaryImageContainer.querySelector('img') as HTMLImageElement;

    this.previousButton = this.container.querySelector('.js-previous');
    this.nextButton = this.container.querySelector('.js-next');

    this.thumbnailsContainer = this.container.querySelector('.js-thumbnails');
    this.thumbnails = Array.from(this.thumbnailsContainer?.querySelectorAll<HTMLImageElement>('img') ?? []);

    if (this.thumbnailsContainer !== null) {
      this.slider = new Splide(this.thumbnailsContainer, {
        perPage: 5,
        arrows: false,
        pagination: false,
      });

      this.slider.mount();

      this.bindThumbnailClick();
      this.bindButtonClick();
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.lightbox = GLightbox({
      touchNavigation: true,
      loop: true,
      elements: this.getLightboxElements(),
    }) as Lightbox;
    this.bindOpenLightbox();
  }

  static initAll(): ProductGallery[] {
    return Array.from(
      document.querySelectorAll<HTMLElement>(ProductGallery.SELECTOR),
    ).map((el) => new ProductGallery(el));
  }

  private bindButtonClick(): void {
    this.previousButton?.addEventListener('click', () => {
      this.index -= 1;
      if (this.index < 0) {
        this.index = this.thumbnails.length - 1;
      }
      this.updateActiveImage();
    });

    this.nextButton?.addEventListener('click', () => {
      this.index += 1;
      if (this.index >= this.thumbnails.length) {
        this.index = 0;
      }
      this.updateActiveImage();
    });
  }

  private bindThumbnailClick(): void {
    if (this.thumbnailsContainer === null) {
      return;
    }

    const thumbnails = Array.from(this.thumbnailsContainer.querySelectorAll('img'));
    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        this.setActiveImage(thumbnail);
        this.openLightbox(Number(thumbnail.dataset.index ?? 0));
      });
    });
  }

  private bindOpenLightbox(): void {
    this.primaryImageContainer.addEventListener('click', (e) => {
      e.preventDefault();
      this.openLightbox(Number(this.primaryImage.dataset.index ?? 0));
    });
  }

  private getLightboxElements(): LightboxElement[] {
    if (this.thumbnails.length) {
      return this.thumbnails.map(imgToLightboxElement);
    }

    return [imgToLightboxElement(this.primaryImageContainer.querySelector('img') as HTMLImageElement)];
  }

  private updateActiveImage(): void {
    this.setActiveImage(this.thumbnails[this.index]);
    if (this.slider) {
      this.slider.go(this.index);
    }
  }

  private setActiveImage(img: HTMLImageElement): void {
    this.primaryImage.src = img.src;
    this.primaryImage.dataset.index = img.dataset.index;
    this.primaryImage.alt = img.alt;
  }

  private openLightbox(index: number): void {
    this.lightbox.openAt(index);
  }
}
